/*
 * XO product-card hover gallery cycle.
 * Uses only real product gallery slides (no placeholder photography):
 *   - 0–1 visible slides: no hover effect
 *   - 2 slides: stock single swap to the second image
 *   - 3+ slides: paced loop through every gallery slide while hovered
 * Patches the public previewImage / resetImage methods on <product-card>,
 * which the card-gallery wires up via on:pointerenter / on:pointerleave.
 */
(() => {
  const DEFAULT_CYCLE_MS = 850;

  const cycleState = new WeakMap();

  const getCycleMs = () => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--xo-duration-card-cycle').trim();
    const seconds = parseFloat(raw);
    return Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds * 1000) : DEFAULT_CYCLE_MS;
  };

  const stopCycle = (card) => {
    const state = cycleState.get(card);
    if (state?.timeoutId) clearTimeout(state.timeoutId);
    cycleState.delete(card);
    return state;
  };

  const startSlideshowCycle = function (card, slideshow) {
    const slides = slideshow.slides;
    if (!slides || slides.length <= 2) return false;

    for (const slide of slides) {
      slide.querySelectorAll('img[loading="lazy"]').forEach((img) => img.removeAttribute('loading'));
    }

    const startIndex = typeof slideshow.current === 'number' ? slideshow.current : 0;
    let pos = startIndex;

    const tick = () => {
      if (!card.isConnected) {
        stopCycle(card);
        return;
      }
      pos = (pos + 1) % slides.length;
      slideshow.select(pos, undefined, { animate: false });
      const timeoutId = setTimeout(tick, getCycleMs());
      cycleState.set(card, { timeoutId, startIndex, slideshow });
    };

    // Show the next (2nd) image immediately, then continue cycling.
    tick();
    return true;
  };

  const patch = (Ctor) => {
    if (!Ctor || Ctor.__xoCyclePatched) return;
    Ctor.__xoCyclePatched = true;

    const originalPreview = Ctor.prototype.previewImage;
    const originalReset = Ctor.prototype.resetImage;

    Ctor.prototype.previewImage = function (event) {
      if (event && event.pointerType !== 'mouse') return;

      stopCycle(this);
      this.resetVariant?.cancel?.();

      const slideshow = this.refs?.slideshow;
      const slides = slideshow?.slides;
      if (!slides || slides.length <= 1) return;
      if (slides.length === 2) return originalPreview.call(this, event);

      startSlideshowCycle(this, slideshow);
    };

    Ctor.prototype.resetImage = function (event) {
      if (event && event.pointerType !== 'mouse') return;

      const slideshow = this.refs?.slideshow;
      const slides = slideshow?.slides;
      if (!slides || slides.length <= 1) return;
      if (slides.length === 2) return originalReset.call(this, event);

      const state = stopCycle(this);
      if (!slideshow) return;

      slideshow.select(state?.startIndex ?? 0, undefined, { animate: false });
    };
  };

  if (window.customElements) {
    customElements.whenDefined('product-card').then(() => patch(customElements.get('product-card')));
  }
})();
